import { useState, useEffect } from 'react';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';
import { AMERICAN_COUNTRIES, isColombia } from '../data/geo';
import LocationFields from './LocationFields';

const DAYS_ES = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
];

const DEFAULT_SCHEDULE = {
  ...Object.fromEntries(
    DAYS_ES.map(({ key }, i) => [key, i < 5
      ? { enabled: true, start: '08:00', end: '18:00', lunch: null }
      : { enabled: false, start: '08:00', end: '18:00', lunch: null }
    ])
  ),
  includeHolidays: false,
};

const inputStyle = {
  width: '100%', padding: '9px 12px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px', color: theme.colors.text,
  fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none',
};

const timeInputStyle = {
  padding: '5px 8px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '6px', color: theme.colors.text,
  fontSize: '0.82rem', outline: 'none', width: '82px',
};

const labelStyle = { display: 'block', color: theme.colors.textMuted, fontSize: '0.85rem', marginBottom: '0.3rem' };
const rowStyle = { marginBottom: '1rem' };

// Campos que cada cita guarda en el contacto; siempre se ofrecen para las variables
// del recordatorio aunque la empresa todavía no haya recibido citas.
const REMINDER_FIELDS = ['nombre', 'fecha_cita', 'hora_cita', 'servicio_cita', 'trabajador_elegido', 'nombre_empresa'];

// Selector de plantilla + un selector de campo personalizado por cada variable del
// body. Lo usan los recordatorios y la confirmación de cita. Al elegir la plantilla
// se leen sus variables del servidor.
function TemplateVarsPicker({ label, templates, templateId, onTemplateChange, mapping, onMappingChange, fields, headers, companyParam }) {
  const [vars, setVars] = useState([]); // [{ index, name }] | null (carrusel)
  useEffect(() => {
    if (!templateId) { setVars([]); return; }
    fetch(`/api/templates/${templateId}/body-variables${companyParam}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => setVars(d ? d.variables : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, companyParam]);

  const options = [...new Set([...fields, ...REMINDER_FIELDS])].sort();
  return (
    <>
      <label style={labelStyle}>{label}</label>
      <select value={templateId} onChange={e => onTemplateChange(e.target.value)} style={{ ...inputStyle, marginBottom: '0.3rem' }}>
        <option value="">Selecciona una plantilla</option>
        {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.status && t.status !== 'APPROVED' ? ` (${t.status})` : ''}</option>)}
      </select>
      {templateId && vars === null && (
        <p style={{ color: theme.colors.error, fontSize: '0.8rem', marginTop: '0.4rem' }}>Las plantillas carrusel no se pueden usar aquí.</p>
      )}
      {templateId && Array.isArray(vars) && vars.length > 0 && (
        <div style={{ marginTop: '0.9rem' }}>
          <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', margin: '0 0 0.5rem' }}>
            ¿De qué campo personalizado sale cada variable de la plantilla?
          </p>
          {vars.map(v => (
            <div key={v.index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ color: theme.colors.text, fontSize: '0.85rem', minWidth: '110px' }}>
                {`{{${v.index}}}`}{v.name !== String(v.index) ? ` ${v.name}` : ''}
              </span>
              <select
                value={mapping[v.name] || ''}
                onChange={e => onMappingChange({ ...mapping, [v.name]: e.target.value })}
                style={{ ...inputStyle, flex: 1, minWidth: '160px', marginBottom: 0 }}
              >
                <option value="">Como está en la plantilla</option>
                {options.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      {templateId && Array.isArray(vars) && vars.length === 0 && (
        <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', marginTop: '0.4rem' }}>Esta plantilla no tiene variables en el cuerpo.</p>
      )}
    </>
  );
}

// Colores por defecto de las vistas públicas (coinciden con el tema base).
const DEFAULT_PUBLIC_THEME = { background: '#0f0f0f', surface: '#1a1a2e', text: '#ffffff', primary: '#38bdf8' };
const THEME_FIELDS = [
  { key: 'background', label: 'Fondo de mi página' },
  { key: 'surface', label: 'Fondo de las tarjetas' },
  { key: 'text', label: 'Color de las letras' },
  { key: 'primary', label: 'Color de botones y títulos' },
];

function Empresa({ token, roleId }) {
  const isMobile = useIsMobile();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [info, setInfo] = useState(null);
  const [pais, setPais] = useState('');
  const [pasarela, setPasarela] = useState('');
  const [savingPais, setSavingPais] = useState(false);
  const [savingPasarela, setSavingPasarela] = useState(false);
  const [msgPais, setMsgPais] = useState('');
  const [msgPasarela, setMsgPasarela] = useState('');

  // Ubicación: departamento, ciudad, barrio, dirección (todos opcionales)
  const [location, setLocation] = useState({ department: '', city: '', district: '', address: '' });
  const [savingLocation, setSavingLocation] = useState(false);
  const [msgLocation, setMsgLocation] = useState('');
  const onLocationChange = (field, value) => setLocation(prev => ({ ...prev, [field]: value }));

  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [msgSchedule, setMsgSchedule] = useState('');

  const [domicileGeneral, setDomicileGeneral] = useState('');
  const [domicileCustom, setDomicileCustom] = useState([]); // [{ dept, value }]
  const [savingDomicile, setSavingDomicile] = useState(false);
  const [msgDomicile, setMsgDomicile] = useState('');

  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [msgLogo, setMsgLogo] = useState('');
  const [tiendaActiva, setTiendaActiva] = useState(false);
  const [savingTienda, setSavingTienda] = useState(false);
  const [citaActiva, setCitaActiva] = useState(false);
  const [savingCita, setSavingCita] = useState(false);
  const [perUserCalendar, setPerUserCalendar] = useState(false);
  const [savingPerUserCalendar, setSavingPerUserCalendar] = useState(false);
  const [buscadorCita, setBuscadorCita] = useState(true);
  const [savingBuscadorCita, setSavingBuscadorCita] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);
  const [msgPaymentMethod, setMsgPaymentMethod] = useState('');
  const [citaPaymentMethod, setCitaPaymentMethod] = useState('onsite');
  const [savingCitaPaymentMethod, setSavingCitaPaymentMethod] = useState(false);
  const [msgCitaPaymentMethod, setMsgCitaPaymentMethod] = useState('');
  // Confirmación de cita: plantilla al cliente apenas agenda
  const [confActiva, setConfActiva] = useState(false);
  const [confTemplateId, setConfTemplateId] = useState('');
  const [confVars, setConfVars] = useState({});
  const [savingConf, setSavingConf] = useState(false);
  const [msgConf, setMsgConf] = useState('');
  // Recordatorios de cita por plantilla de WhatsApp
  const [recActivo, setRecActivo] = useState(false);
  const [recTemplateId, setRecTemplateId] = useState('');
  const [recVars, setRecVars] = useState({}); // { variable del body: campo personalizado }
  const [recHoras, setRecHoras] = useState('2');
  const [respActiva, setRespActiva] = useState(false);
  const [emailResp, setEmailResp] = useState('');
  const [passResp, setPassResp] = useState('');
  const [hasPassResp, setHasPassResp] = useState(false);
  const [costoPlantilla, setCostoPlantilla] = useState(0);
  const [customFields, setCustomFields] = useState([]);
  const [savingRec, setSavingRec] = useState(false);
  const [msgRec, setMsgRec] = useState('');
  const [notifAdminApp, setNotifAdminApp] = useState(false);
  const [notifAdminCorreo, setNotifAdminCorreo] = useState(false);
  const [emailNotifAdmin, setEmailNotifAdmin] = useState('');
  const [passNotifAdmin, setPassNotifAdmin] = useState('');
  const [hasPassNotifAdmin, setHasPassNotifAdmin] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [savingNotifAdmin, setSavingNotifAdmin] = useState(false);
  const [msgNotifAdmin, setMsgNotifAdmin] = useState('');
  const [agentsCanStart, setAgentsCanStart] = useState(false);
  const [savingStart, setSavingStart] = useState(false);
  const [msgStart, setMsgStart] = useState('');
  const [ticketsActiva, setTicketsActiva] = useState(false);
  const [savingTicketsActiva, setSavingTicketsActiva] = useState(false);
  const [ticketTemplateId, setTicketTemplateId] = useState('');
  const [ticketSendComments, setTicketSendComments] = useState(false);
  const [savingTicketConfig, setSavingTicketConfig] = useState(false);
  const [msgTicketConfig, setMsgTicketConfig] = useState('');
  const [ticketTemplates, setTicketTemplates] = useState([]);
  const [newsActiva, setNewsActiva] = useState(false);
  const [savingNews, setSavingNews] = useState(false);
  const [carouselTienda, setCarouselTienda] = useState(false);
  const [carouselCita, setCarouselCita] = useState(false);
  const [carouselImages, setCarouselImages] = useState([]);
  const [carouselInterval, setCarouselInterval] = useState('4');
  const [savingCarousel, setSavingCarousel] = useState(false);
  const [uploadingCarousel, setUploadingCarousel] = useState(false);
  const [msgCarousel, setMsgCarousel] = useState('');
  const [showTheme, setShowTheme] = useState(false);
  const [pubTheme, setPubTheme] = useState({ ...DEFAULT_PUBLIC_THEME });
  const [savingTheme, setSavingTheme] = useState(false);
  const [msgTheme, setMsgTheme] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (roleId !== 1) return;
    fetch('/api/companies?page=1', { headers })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => {
        setCompanies(d.data || []);
        if (d.data?.length > 0) setSelectedCompanyId(String(d.data[0].id));
      });
  }, [roleId, token]);

  useEffect(() => {
    const companyParam = roleId === 1 && selectedCompanyId ? `?company_id=${selectedCompanyId}` : '';
    if (roleId === 1 && !selectedCompanyId) return;
    fetch(`/api/pay/company-info${companyParam}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setInfo(d);
        setPais(d.pais || '');
        setLocation({ department: d.department || '', city: d.city || '', district: d.district || '', address: d.address || '' });
        setLogoUrl(d.logo || '');
        setTiendaActiva(!!d.tienda_activa);
        setCitaActiva(!!d.cita_activa);
        setPerUserCalendar(!!d.per_user_calendar);
        setBuscadorCita(d.buscador_cita_activa !== false);
        setPaymentMethod(d.payment_method || 'online');
        setCitaPaymentMethod(d.cita_payment_method || 'onsite');
        setAgentsCanStart(!!d.agents_can_start);
        setNotifAdminApp(!!d.notificar_admin_app);
        setNotifAdminCorreo(!!d.notificar_admin_correo);
        setEmailNotifAdmin(d.email_notificacion_admin || '');
        setHasPassNotifAdmin(!!d.has_password_api_notificacion_admin);
        setIsPremium(!!d.is_premium);
        setConfActiva(!!d.confirmacion_activa);
        setConfTemplateId(d.confirmacion_template_id ? String(d.confirmacion_template_id) : '');
        setConfVars(d.confirmacion_variables || {});
        setRecActivo(!!d.recordatorio_activo);
        setRecTemplateId(d.recordatorio_template_id ? String(d.recordatorio_template_id) : '');
        setRecVars(d.recordatorio_variables || {});
        setRecHoras(String(d.recordatorio_horas || 2));
        setRespActiva(!!d.respuesta_plantilla_activa);
        setEmailResp(d.email_respuesta || '');
        setHasPassResp(!!d.has_password_api_respuesta);
        setCostoPlantilla(Number(d.costo_plantilla) || 0);
        setTicketsActiva(!!d.tickets_activa);
        setTicketTemplateId(d.ticket_template_id ? String(d.ticket_template_id) : '');
        setTicketSendComments(!!d.ticket_send_comments);
        setNewsActiva(!!d.news_activa);
        setCarouselTienda(!!d.carousel_tienda_activa);
        setCarouselCita(!!d.carousel_cita_activa);
        setCarouselImages(Array.isArray(d.carousel_images) ? d.carousel_images : []);
        setCarouselInterval(d.carousel_interval ? String(d.carousel_interval) : '4');
        setPubTheme({ ...DEFAULT_PUBLIC_THEME, ...(d.public_theme || {}) });
        setShowTheme(!!d.public_theme);
        const dom = d.domicile_value || {};
        setDomicileGeneral(dom.general !== undefined && dom.general !== null ? String(dom.general) : '');
        setDomicileCustom(Object.entries(dom.personalizado || {}).map(([dept, value]) => ({ dept, value: String(value) })));
      });

    const schedParam = roleId === 1 && selectedCompanyId ? `?company_id=${selectedCompanyId}` : '';
    fetch(`/api/appointments/schedule${schedParam}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.schedule) setSchedule(d.schedule); });

    const tplParam = roleId === 1 && selectedCompanyId ? `?company_id=${selectedCompanyId}` : '';
    fetch(`/api/templates/list${tplParam}`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setTicketTemplates(Array.isArray(d) ? d : []));
    fetch(`/api/templates/custom-fields${tplParam}`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d.map(f => f.name) : []));
  }, [selectedCompanyId, roleId, token]);


  const savePais = async () => {
    setSavingPais(true);
    setMsgPais('');
    const body = roleId === 1 && selectedCompanyId ? { pais, company_id: Number(selectedCompanyId) } : { pais };
    const res = await fetch('/api/pay/company-pais', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgPais(res.ok ? '✓ País actualizado' : 'Error al guardar');
    setSavingPais(false);
  };

  const saveLocation = async () => {
    setSavingLocation(true);
    setMsgLocation('');
    const body = roleId === 1 && selectedCompanyId
      ? { ...location, company_id: Number(selectedCompanyId) }
      : { ...location };
    const res = await fetch('/api/pay/company-location', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgLocation(res.ok ? '✓ Ubicación guardada' : 'Error al guardar');
    setSavingLocation(false);
  };

  const savePasarela = async () => {
    if (!pasarela.trim()) return setMsgPasarela('La clave no puede estar vacía');
    setSavingPasarela(true);
    setMsgPasarela('');
    const body = roleId === 1 && selectedCompanyId ? { pasarela, company_id: Number(selectedCompanyId) } : { pasarela };
    const res = await fetch('/api/pay/company-pasarela', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setMsgPasarela('✓ Clave guardada correctamente');
      setPasarela('');
      setInfo(prev => prev ? { ...prev, has_pasarela: true } : prev);
    } else {
      setMsgPasarela('Error al guardar');
    }
    setSavingPasarela(false);
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    setMsgSchedule('');
    const body = roleId === 1 && selectedCompanyId
      ? { schedule, company_id: Number(selectedCompanyId) }
      : { schedule };
    const res = await fetch('/api/appointments/schedule', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgSchedule(res.ok ? '✓ Horario guardado' : 'Error al guardar');
    setSavingSchedule(false);
  };

  const updateDay = (key, field, value) => {
    setSchedule(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const toggleLunch = (key, enable) => {
    setSchedule(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        lunch: enable ? { start: '12:00', end: '13:00' } : null,
      },
    }));
  };

  const updateLunch = (key, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [key]: { ...prev[key], lunch: { ...prev[key].lunch, [field]: value } },
    }));
  };

  const addDomicileRow = () => setDomicileCustom(prev => [...prev, { dept: '', value: '' }]);
  const updateDomicileRow = (i, field, val) => setDomicileCustom(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const removeDomicileRow = (i) => setDomicileCustom(prev => prev.filter((_, idx) => idx !== i));

  const saveDomicile = async () => {
    setSavingDomicile(true);
    setMsgDomicile('');
    const personalizado = {};
    domicileCustom.forEach(({ dept, value }) => {
      const d = dept.trim();
      if (d) personalizado[d] = parseInt(value) || 0;
    });
    const domicile_value = { general: parseInt(domicileGeneral) || 0, personalizado };
    const body = roleId === 1 && selectedCompanyId
      ? { domicile_value, company_id: Number(selectedCompanyId) }
      : { domicile_value };
    const res = await fetch('/api/pay/company-domicile', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgDomicile(res.ok ? '✓ Domicilios guardados' : 'Error al guardar');
    setSavingDomicile(false);
  };

  const uploadLogoFile = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    setMsgLogo('');
    const fd = new FormData();
    fd.append('logo', file);
    if (roleId === 1 && selectedCompanyId) fd.append('company_id', selectedCompanyId);
    const res = await fetch('/api/pay/company-logo', { method: 'POST', headers, body: fd });
    if (res.ok) {
      const d = await res.json();
      setLogoUrl(d.logo);
      setMsgLogo('✓ Logo actualizado');
    } else {
      setMsgLogo('Error al subir el logo');
    }
    setUploadingLogo(false);
  };

  const saveTienda = async (value) => {
    setTiendaActiva(value);
    setSavingTienda(true);
    const body = roleId === 1 && selectedCompanyId
      ? { tienda_activa: value, company_id: Number(selectedCompanyId) }
      : { tienda_activa: value };
    await fetch('/api/pay/company-store', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSavingTienda(false);
  };

  const saveCita = async (value) => {
    setCitaActiva(value);
    setSavingCita(true);
    const body = roleId === 1 && selectedCompanyId
      ? { cita_activa: value, company_id: Number(selectedCompanyId) }
      : { cita_activa: value };
    await fetch('/api/pay/company-cita', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSavingCita(false);
  };

  const savePerUserCalendar = async (value) => {
    setPerUserCalendar(value);
    setSavingPerUserCalendar(true);
    const body = roleId === 1 && selectedCompanyId
      ? { per_user_calendar: value, company_id: Number(selectedCompanyId) }
      : { per_user_calendar: value };
    await fetch('/api/pay/company-per-user-calendar', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSavingPerUserCalendar(false);
  };

  const saveBuscadorCita = async (value) => {
    setBuscadorCita(value);
    setSavingBuscadorCita(true);
    const body = roleId === 1 && selectedCompanyId
      ? { buscador_cita_activa: value, company_id: Number(selectedCompanyId) }
      : { buscador_cita_activa: value };
    await fetch('/api/pay/company-buscador-cita', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSavingBuscadorCita(false);
  };

  const savePaymentMethod = async (value) => {
    setPaymentMethod(value);
    setSavingPaymentMethod(true);
    setMsgPaymentMethod('');
    const body = roleId === 1 && selectedCompanyId
      ? { payment_method: value, company_id: Number(selectedCompanyId) }
      : { payment_method: value };
    const res = await fetch('/api/pay/company-payment-method', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgPaymentMethod(res.ok ? '✓ Guardado' : 'Error al guardar');
    setSavingPaymentMethod(false);
  };

  const saveCitaPaymentMethod = async (value) => {
    setCitaPaymentMethod(value);
    setSavingCitaPaymentMethod(true);
    setMsgCitaPaymentMethod('');
    const body = roleId === 1 && selectedCompanyId
      ? { cita_payment_method: value, company_id: Number(selectedCompanyId) }
      : { cita_payment_method: value };
    const res = await fetch('/api/pay/company-cita-payment-method', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgCitaPaymentMethod(res.ok ? '✓ Guardado' : 'Error al guardar');
    setSavingCitaPaymentMethod(false);
  };

  // Guarda la confirmación de cita. Desmarcar desactiva al instante; activar se
  // guarda con el botón (hay que elegir la plantilla primero).
  const saveConfirmacion = async (activa) => {
    setSavingConf(true);
    setMsgConf('');
    const payload = {
      confirmacion_activa: activa,
      confirmacion_template_id: confTemplateId ? Number(confTemplateId) : null,
      confirmacion_variables: confVars,
    };
    if (roleId === 1 && selectedCompanyId) payload.company_id = Number(selectedCompanyId);
    try {
      const res = await fetch('/api/pay/company-confirmacion', {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setMsgConf(activa ? '✓ Confirmación de cita guardada' : '✓ Confirmación de cita desactivada');
        if (data && data.confirmacion_variables) setConfVars(data.confirmacion_variables);
      } else {
        setMsgConf((data && data.message) || 'Error al guardar');
        if (!activa) setConfActiva(true);
      }
    } catch {
      setMsgConf('Error al guardar');
    }
    setSavingConf(false);
  };

  // Guarda la sección de recordatorios. Desmarcar el checkbox desactiva al instante;
  // el resto se guarda con el botón.
  const saveRecordatorio = async (activo) => {
    setSavingRec(true);
    setMsgRec('');
    const payload = {
      recordatorio_activo: activo,
      recordatorio_template_id: recTemplateId ? Number(recTemplateId) : null,
      recordatorio_variables: recVars,
      recordatorio_horas: Number(recHoras),
      respuesta_plantilla_activa: respActiva,
      email_respuesta: emailResp,
      password_api_respuesta: passResp,
    };
    if (roleId === 1 && selectedCompanyId) payload.company_id = Number(selectedCompanyId);
    try {
      const res = await fetch('/api/pay/company-recordatorio', {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setMsgRec(activo ? '✓ Recordatorios guardados' : '✓ Recordatorios desactivados');
        if (activo && passResp) { setHasPassResp(true); setPassResp(''); }
        if (data && data.recordatorio_variables) setRecVars(data.recordatorio_variables);
      } else {
        setMsgRec((data && data.message) || 'Error al guardar');
        if (!activo) setRecActivo(true); // no se pudo desactivar
      }
    } catch {
      setMsgRec('Error al guardar');
    }
    setSavingRec(false);
  };

  // field: 'notificar_admin_app' | 'notificar_admin_correo'
  const saveNotifAdmin = async (field, checked) => {
    const setter = field === 'notificar_admin_app' ? setNotifAdminApp : setNotifAdminCorreo;
    setter(checked);
    setSavingNotifAdmin(true);
    setMsgNotifAdmin('');
    const body = roleId === 1 && selectedCompanyId
      ? { [field]: checked, company_id: Number(selectedCompanyId) }
      : { [field]: checked };
    try {
      const res = await fetch('/api/pay/company-admin-notifications', {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setMsgNotifAdmin('✓ Guardado');
      } else {
        setter(!checked); // revertir si el backend rechazó el cambio
        const data = await res.json().catch(() => null);
        setMsgNotifAdmin((data && data.message) || 'Error al guardar');
      }
    } catch {
      setter(!checked);
      setMsgNotifAdmin('Error al guardar');
    }
    setSavingNotifAdmin(false);
  };

  // Activa el aviso por correo con el correo y la contraseña API de la empresa.
  // Si el servidor lo rechaza, el checkbox queda marcado con el error a la vista
  // para que corrijan los datos sin volver a escribirlos.
  const saveCorreoAdmin = async () => {
    setSavingNotifAdmin(true);
    setMsgNotifAdmin('');
    const body = {
      notificar_admin_correo: true,
      email_notificacion_admin: emailNotifAdmin,
      password_api_notificacion_admin: passNotifAdmin,
    };
    if (roleId === 1 && selectedCompanyId) body.company_id = Number(selectedCompanyId);
    try {
      const res = await fetch('/api/pay/company-admin-notifications', {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setMsgNotifAdmin('✓ Guardado');
        if (passNotifAdmin) { setHasPassNotifAdmin(true); setPassNotifAdmin(''); }
      } else {
        const data = await res.json().catch(() => null);
        setMsgNotifAdmin((data && data.message) || 'Error al guardar');
      }
    } catch {
      setMsgNotifAdmin('Error al guardar');
    }
    setSavingNotifAdmin(false);
  };

  const saveAgentsCanStart = async (checked) => {
    setAgentsCanStart(checked);
    setSavingStart(true);
    setMsgStart('');
    const body = roleId === 1 && selectedCompanyId
      ? { enabled: checked, company_id: Number(selectedCompanyId) }
      : { enabled: checked };
    try {
      const res = await fetch('/api/pay/company-agents-start', {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setMsgStart('✓ Guardado');
      } else {
        setAgentsCanStart(!checked); // revertir si el backend rechazó el cambio
        setMsgStart('Error al guardar');
      }
    } catch {
      setAgentsCanStart(!checked); // revertir ante fallo de red
      setMsgStart('Error al guardar');
    } finally {
      setSavingStart(false);
    }
  };

  const saveTicketsActiva = async (value) => {
    setTicketsActiva(value);
    setSavingTicketsActiva(true);
    const body = roleId === 1 && selectedCompanyId
      ? { tickets_activa: value, company_id: Number(selectedCompanyId) }
      : { tickets_activa: value };
    await fetch('/api/pay/company-tickets', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSavingTicketsActiva(false);
  };

  const saveTicketConfig = async (changes) => {
    setSavingTicketConfig(true);
    setMsgTicketConfig('');
    const body = roleId === 1 && selectedCompanyId
      ? { ...changes, company_id: Number(selectedCompanyId) }
      : { ...changes };
    const res = await fetch('/api/pay/company-ticket-config', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgTicketConfig(res.ok ? '✓ Guardado' : 'Error al guardar');
    setSavingTicketConfig(false);
  };

  const saveNews = async (value) => {
    setNewsActiva(value);
    setSavingNews(true);
    const body = roleId === 1 && selectedCompanyId
      ? { news_activa: value, company_id: Number(selectedCompanyId) }
      : { news_activa: value };
    await fetch('/api/pay/company-news', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSavingNews(false);
  };

  // Activa/desactiva el carrusel para tienda o citas. `changes` puede traer
  // carousel_tienda_activa, carousel_cita_activa y/o carousel_interval.
  const saveCarouselConfig = async (changes) => {
    setSavingCarousel(true);
    setMsgCarousel('');
    const body = roleId === 1 && selectedCompanyId
      ? { ...changes, company_id: Number(selectedCompanyId) }
      : { ...changes };
    const res = await fetch('/api/pay/company-carousel-config', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgCarousel(res.ok ? '✓ Carrusel actualizado' : 'Error al guardar');
    setSavingCarousel(false);
  };

  const toggleCarouselTienda = (value) => { setCarouselTienda(value); saveCarouselConfig({ carousel_tienda_activa: value }); };
  const toggleCarouselCita = (value) => { setCarouselCita(value); saveCarouselConfig({ carousel_cita_activa: value }); };

  const saveCarouselInterval = () => {
    const secs = Math.min(60, Math.max(1, parseInt(carouselInterval, 10) || 4));
    setCarouselInterval(String(secs));
    saveCarouselConfig({ carousel_interval: secs });
  };

  const uploadCarouselImages = async (files) => {
    if (!files || !files.length) return;
    setUploadingCarousel(true);
    setMsgCarousel('');
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('images', f));
    if (roleId === 1 && selectedCompanyId) fd.append('company_id', selectedCompanyId);
    const res = await fetch('/api/pay/company-carousel-images', { method: 'POST', headers, body: fd });
    if (res.ok) {
      const d = await res.json();
      setCarouselImages(d.images || []);
      setMsgCarousel('✓ Imágenes agregadas');
    } else {
      const d = await res.json().catch(() => ({}));
      setMsgCarousel(d.message || 'Error al subir las imágenes');
    }
    setUploadingCarousel(false);
  };

  const removeCarouselImage = async (url) => {
    const next = carouselImages.filter(u => u !== url);
    setCarouselImages(next);
    const body = roleId === 1 && selectedCompanyId
      ? { images: next, company_id: Number(selectedCompanyId) }
      : { images: next };
    await fetch('/api/pay/company-carousel-images', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  };

  const savePublicTheme = async () => {
    setSavingTheme(true);
    setMsgTheme('');
    const body = roleId === 1 && selectedCompanyId
      ? { public_theme: pubTheme, company_id: Number(selectedCompanyId) }
      : { public_theme: pubTheme };
    const res = await fetch('/api/pay/company-theme', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgTheme(res.ok ? '✓ Colores guardados' : 'Error al guardar');
    setSavingTheme(false);
  };

  const resetPublicTheme = async () => {
    setSavingTheme(true);
    setMsgTheme('');
    setPubTheme({ ...DEFAULT_PUBLIC_THEME });
    const body = roleId === 1 && selectedCompanyId
      ? { public_theme: null, company_id: Number(selectedCompanyId) }
      : { public_theme: null };
    const res = await fetch('/api/pay/company-theme', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setMsgTheme(res.ok ? '✓ Restablecido al tema por defecto' : 'Error al guardar');
    setSavingTheme(false);
  };

  const storeSlug = info?.name ? info.name.replace(/ /g, '_') : '';
  const isColombiaPais = isColombia(pais);

  const btnStyle = (saving) => ({
    padding: '7px 20px',
    backgroundColor: saving ? theme.colors.border : theme.colors.primary,
    color: theme.colors.background,
    border: 'none', borderRadius: '7px',
    cursor: saving ? 'not-allowed' : 'pointer',
    fontSize: '0.88rem', fontWeight: '700',
  });

  return (
    <main style={{ padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem', maxWidth: '760px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.2rem' : '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>
        <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', marginBottom: '1.5rem' }}>Mi Empresa</h1>

        {roleId === 1 && companies.length > 0 && (
          <div style={rowStyle}>
            <label style={labelStyle}>Empresa</label>
            <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {info && (
          <>
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.8rem' }}>
                {[['Nombre', info.name], ['Teléfono', info.phone], ['Zona horaria', info.timezone], ['Descripción', info.description]].map(([label, val]) => (
                  <div key={label}>
                    <p style={{ color: theme.colors.textMuted, fontSize: '0.75rem', margin: '0 0 2px' }}>{label}</p>
                    <p style={{ color: theme.colors.text, fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>{val || '—'}</p>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ ...rowStyle, marginBottom: '1.5rem' }}>
              <label style={labelStyle}>País</label>
              <select value={pais} onChange={e => setPais(e.target.value)} style={{ ...inputStyle, cursor: 'pointer', marginBottom: '0.5rem' }}>
                <option value="">— Seleccionar país —</option>
                {AMERICAN_COUNTRIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <button onClick={savePais} disabled={savingPais} style={btnStyle(savingPais)}>
                {savingPais ? 'Guardando...' : 'Guardar país'}
              </button>
              {msgPais && <p style={{ color: theme.colors.success, fontSize: '0.82rem', marginTop: '0.4rem' }}>{msgPais}</p>}
            </div>

            {/* Ubicación de la empresa */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '0.6rem' }}>Ubicación de la empresa</h2>
              <div style={{ backgroundColor: `${theme.colors.primary}11`, border: `1px solid ${theme.colors.primary}55`, borderRadius: '8px', padding: '0.8rem 1rem', marginBottom: '1rem' }}>
                <p style={{ color: theme.colors.text, fontSize: '0.82rem', lineHeight: 1.5, margin: 0 }}>
                  📍 Tu empresa tendrá <strong>mejor cogida</strong> en las vitrinas si llenas estos campos.
                  No son obligatorios. {isColombiaPais ? 'Como tu país es Colombia, elige departamento y ciudad de la lista.' : 'Escribe los datos según tu país.'}
                </p>
              </div>
              <LocationFields
                pais={pais}
                department={location.department}
                city={location.city}
                district={location.district}
                address={location.address}
                onChange={onLocationChange}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
              />
              <button onClick={saveLocation} disabled={savingLocation} style={btnStyle(savingLocation)}>
                {savingLocation ? 'Guardando...' : 'Guardar ubicación'}
              </button>
              {msgLocation && <p style={{ color: msgLocation.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.4rem' }}>{msgLocation}</p>}
            </div>

            {/* Horario de citas */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '1rem' }}>Horario de citas</h2>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  checked={schedule.includeHolidays === true}
                  onChange={e => setSchedule(prev => ({ ...prev, includeHolidays: e.target.checked }))}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.85rem' }}>Atender en días festivos</span>
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {DAYS_ES.map(({ key, label }) => {
                  const day = schedule[key] || { enabled: false, start: '08:00', end: '18:00', lunch: null };
                  return (
                    <div key={key} style={{ borderBottom: `1px solid ${theme.colors.border}22`, paddingBottom: '0.6rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', minWidth: '90px' }}>
                          <input
                            type="checkbox"
                            checked={day.enabled}
                            onChange={e => updateDay(key, 'enabled', e.target.checked)}
                            style={{ accentColor: theme.colors.primary }}
                          />
                          <span style={{ color: day.enabled ? theme.colors.text : theme.colors.textMuted, fontSize: '0.88rem', fontWeight: day.enabled ? 600 : 400 }}>{label}</span>
                        </label>
                        {day.enabled && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Inicio</span>
                              <input type="time" value={day.start} onChange={e => updateDay(key, 'start', e.target.value)} style={timeInputStyle} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Fin</span>
                              <input type="time" value={day.end} onChange={e => updateDay(key, 'end', e.target.value)} style={timeInputStyle} />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={!!day.lunch}
                                onChange={e => toggleLunch(key, e.target.checked)}
                                style={{ accentColor: theme.colors.primary }}
                              />
                              <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Almuerzo</span>
                            </label>
                            {day.lunch && (
                              <>
                                <input type="time" value={day.lunch.start} onChange={e => updateLunch(key, 'start', e.target.value)} style={timeInputStyle} />
                                <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>–</span>
                                <input type="time" value={day.lunch.end} onChange={e => updateLunch(key, 'end', e.target.value)} style={timeInputStyle} />
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <button onClick={saveSchedule} disabled={savingSchedule} style={btnStyle(savingSchedule)}>
                  {savingSchedule ? 'Guardando...' : 'Guardar horario'}
                </button>
                {msgSchedule && <p style={{ color: msgSchedule.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.4rem' }}>{msgSchedule}</p>}
              </div>
            </div>

            {/* Logo y tienda pública */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '1rem' }}>Logo y tienda pública</h2>
              {storeSlug && (
                <div style={{ backgroundColor: `${theme.colors.primary}11`, border: `1px solid ${theme.colors.primary}`, borderRadius: '10px', padding: '0.9rem 1rem', marginBottom: '1.2rem' }}>
                  <p style={{ color: theme.colors.text, fontSize: '0.85rem', lineHeight: 1.55, margin: '0 0 0.5rem' }}>
                    🔗 <strong>Un solo link para tus clientes.</strong> Reúne todas las secciones que actives abajo (tienda, citas, PQR y noticias) en una sola página con menú de navegación arriba:
                  </p>
                  <a href={`/p/${storeSlug}`} target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary, fontWeight: 600, fontSize: '0.88rem', wordBreak: 'break-all' }}>
                    {window.location.origin}/p/{storeSlug}
                  </a>
                </div>
              )}
              {logoUrl && (
                <img src={logoUrl} alt="Logo" style={{ width: '90px', height: '90px', objectFit: 'contain', borderRadius: '8px', border: `1px solid ${theme.colors.border}`, marginBottom: '0.8rem', display: 'block' }} />
              )}
              <label style={labelStyle}>Logo de la empresa</label>
              <input
                type="file"
                accept="image/*"
                onChange={e => uploadLogoFile(e.target.files?.[0])}
                disabled={uploadingLogo}
                style={{ ...inputStyle, marginBottom: '0.5rem', cursor: 'pointer' }}
              />
              {msgLogo && <p style={{ color: msgLogo.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginBottom: '0.8rem' }}>{msgLogo}</p>}

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '0.8rem' }}>
                <input
                  type="checkbox"
                  checked={tiendaActiva}
                  disabled={savingTienda}
                  onChange={e => saveTienda(e.target.checked)}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>Activar tienda pública</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                <input
                  type="checkbox"
                  checked={carouselTienda}
                  disabled={savingCarousel}
                  onChange={e => toggleCarouselTienda(e.target.checked)}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Activar carrusel de imágenes en la tienda</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '1rem' }}>
                <input
                  type="checkbox"
                  checked={citaActiva}
                  disabled={savingCita}
                  onChange={e => saveCita(e.target.checked)}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>Activar agendamiento de citas público</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginTop: '0.8rem' }}>
                <input
                  type="checkbox"
                  checked={perUserCalendar}
                  disabled={savingPerUserCalendar}
                  onChange={e => savePerUserCalendar(e.target.checked)}
                  style={{ accentColor: theme.colors.primary, marginTop: '3px' }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>
                  Calendario diferente para cada usuario
                  <span style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '2px' }}>
                    Cada usuario (administrador y trabajadores) define su propio horario y servicios en “Configuración horario”, y el cliente elige con quién agendar. Si lo desactivas, se usa un solo calendario global (el de arriba).
                  </span>
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                <input
                  type="checkbox"
                  checked={carouselCita}
                  disabled={savingCarousel}
                  onChange={e => toggleCarouselCita(e.target.checked)}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Activar carrusel de imágenes en las citas</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                <input
                  type="checkbox"
                  checked={buscadorCita}
                  disabled={savingBuscadorCita}
                  onChange={e => saveBuscadorCita(e.target.checked)}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Activar buscador en las citas (servicio y precio mín./máx.)</span>
              </label>

              {/* Configuración del carrusel: aparece si está activo en tienda o citas */}
              {(carouselTienda || carouselCita) && (
                <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '10px' }}>
                  <h3 style={{ color: theme.colors.primary, fontSize: '0.92rem', margin: '0 0 0.3rem' }}>Imágenes del carrusel</h3>
                  <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', margin: '0 0 0.8rem' }}>
                    Estas imágenes se muestran arriba del catálogo. Se usan tanto en la tienda como en las citas según lo que actives. Máximo 12 imágenes.
                  </p>

                  {carouselImages.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.9rem' }}>
                      {carouselImages.map((url) => (
                        <div key={url} style={{ position: 'relative', width: '96px', height: '64px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme.colors.border}` }}>
                          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          <button
                            onClick={() => removeCarouselImage(url)}
                            title="Eliminar imagen"
                            style={{ position: 'absolute', top: '3px', right: '3px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <label style={labelStyle}>Agregar imágenes desde tu computador</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={e => { uploadCarouselImages(e.target.files); e.target.value = ''; }}
                    disabled={uploadingCarousel || carouselImages.length >= 12}
                    style={{ ...inputStyle, marginBottom: '0.8rem', cursor: 'pointer' }}
                  />
                  {carouselImages.length >= 12 && (
                    <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', margin: '-0.4rem 0 0.8rem' }}>Alcanzaste el máximo de 12 imágenes. Elimina alguna para agregar más.</p>
                  )}

                  <label style={labelStyle}>Segundos de transición entre imágenes</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="number"
                      onWheel={e => e.currentTarget.blur()}
                      min="1"
                      max="60"
                      value={carouselInterval}
                      onChange={e => setCarouselInterval(e.target.value)}
                      style={{ ...inputStyle, width: '120px' }}
                    />
                    <button onClick={saveCarouselInterval} disabled={savingCarousel} style={btnStyle(savingCarousel)}>
                      {savingCarousel ? 'Guardando...' : 'Guardar segundos'}
                    </button>
                  </div>
                  {uploadingCarousel && <p style={{ color: theme.colors.textMuted, fontSize: '0.82rem', marginTop: '0.5rem', marginBottom: 0 }}>Subiendo imágenes...</p>}
                  {msgCarousel && <p style={{ color: msgCarousel.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.5rem', marginBottom: 0 }}>{msgCarousel}</p>}
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '1rem' }}>
                <input
                  type="checkbox"
                  checked={ticketsActiva}
                  disabled={savingTicketsActiva}
                  onChange={e => saveTicketsActiva(e.target.checked)}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>Activar portal público de tickets (PQR)</span>
              </label>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1.2rem' }}>
                <div style={{ flex: '1 1 240px' }}>
                  <label style={labelStyle}>Plantilla para comentarios de tickets</label>
                  <select
                    value={ticketTemplateId}
                    disabled={savingTicketConfig}
                    onChange={e => { setTicketTemplateId(e.target.value); saveTicketConfig({ ticket_template_id: e.target.value || null }); }}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    <option value="">— Sin plantilla —</option>
                    {ticketTemplates.map(t => <option key={t.id} value={t.id}>{t.name}{t.status && t.status !== 'APPROVED' ? ` (${t.status})` : ''}</option>)}
                  </select>
                  <p style={{ color: theme.colors.textMuted, fontSize: '0.74rem', marginTop: '0.3rem' }}>
                    Debe ser una plantilla con una sola variable: ahí se inserta el comentario.
                  </p>
                </div>
                <div style={{ flex: '1 1 240px' }}>
                  <label style={labelStyle}>Activar envío de comentarios al cliente</label>
                  <select
                    value={ticketSendComments ? 'true' : 'false'}
                    disabled={savingTicketConfig}
                    onChange={e => { const v = e.target.value === 'true'; setTicketSendComments(v); saveTicketConfig({ ticket_send_comments: v }); }}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    <option value="false">No</option>
                    <option value="true">Sí</option>
                  </select>
                </div>
              </div>
              {msgTicketConfig && <p style={{ color: msgTicketConfig.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.5rem' }}>{msgTicketConfig}</p>}

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '1.2rem' }}>
                <input
                  type="checkbox"
                  checked={newsActiva}
                  disabled={savingNews}
                  onChange={e => saveNews(e.target.checked)}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>Activar noticias públicas</span>
              </label>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1.2rem' }}>
                <div style={{ flex: '1 1 240px' }}>
                  <label style={labelStyle}>Método de pago de la tienda</label>
                  <select
                    value={paymentMethod}
                    disabled={savingPaymentMethod}
                    onChange={e => savePaymentMethod(e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    {/* El pago en línea requiere la clave de Wompi configurada; si no
                        la tiene, la pasarela no funciona. Solo se ofrece a quienes la
                        tengan (o si ya estaba guardado, para no ocultar su valor actual). */}
                    {(info?.has_pasarela || paymentMethod === 'online') && <option value="online">Pago en línea</option>}
                    <option value="cod">Pago contraentrega</option>
                    {(info?.has_pasarela || paymentMethod === 'both') && <option value="both">Ambos</option>}
                  </select>
                  {!info?.has_pasarela && (
                    <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '0.4rem' }}>
                      Configura tu clave de Wompi para habilitar el pago en línea.
                    </p>
                  )}
                  {msgPaymentMethod && <p style={{ color: msgPaymentMethod.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.4rem' }}>{msgPaymentMethod}</p>}
                </div>
                <div style={{ flex: '1 1 240px' }}>
                  <label style={labelStyle}>Método de pago de las citas</label>
                  <select
                    value={citaPaymentMethod}
                    disabled={savingCitaPaymentMethod}
                    onChange={e => saveCitaPaymentMethod(e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    {(info?.has_pasarela || citaPaymentMethod === 'online') && <option value="online">Pago en línea</option>}
                    <option value="onsite">Pagar el día de la cita</option>
                    {(info?.has_pasarela || citaPaymentMethod === 'both') && <option value="both">Ambos</option>}
                  </select>
                  {!info?.has_pasarela && (
                    <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '0.4rem' }}>
                      Configura tu clave de Wompi para habilitar el pago en línea.
                    </p>
                  )}
                  {msgCitaPaymentMethod && <p style={{ color: msgCitaPaymentMethod.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.4rem' }}>{msgCitaPaymentMethod}</p>}
                </div>
              </div>
            </div>

            {/* Personalizar mi empresa (colores de las vistas públicas) */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <button
                onClick={() => setShowTheme(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%', textAlign: 'left' }}
              >
                <h2 style={{ color: theme.colors.primary, fontSize: '1rem', margin: 0 }}>Personalizar mi empresa</h2>
                <span style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginLeft: 'auto' }}>{showTheme ? '▲' : '▼'}</span>
              </button>
              <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
                Elige los colores de tus vistas públicas (tienda, citas, tickets y noticias).
              </p>

              {showTheme && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.9rem' }}>
                    {THEME_FIELDS.map(({ key, label }) => (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                        <input
                          type="color"
                          value={pubTheme[key] || DEFAULT_PUBLIC_THEME[key]}
                          onChange={e => setPubTheme(prev => ({ ...prev, [key]: e.target.value }))}
                          style={{ width: '46px', height: '38px', padding: 0, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', background: 'none', cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div>
                          <span style={{ color: theme.colors.text, fontSize: '0.85rem', display: 'block' }}>{label}</span>
                          <span style={{ color: theme.colors.textMuted, fontSize: '0.75rem', fontFamily: 'monospace' }}>{(pubTheme[key] || DEFAULT_PUBLIC_THEME[key]).toUpperCase()}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Vista previa rápida de los colores elegidos */}
                  <div style={{ marginTop: '1rem', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${theme.colors.border}`, backgroundColor: pubTheme.background }}>
                    <div style={{ padding: '0.9rem 1rem' }}>
                      <p style={{ color: pubTheme.primary, fontWeight: 700, margin: '0 0 0.6rem', fontSize: '0.95rem' }}>Vista previa</p>
                      <div style={{ backgroundColor: pubTheme.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', padding: '0.8rem' }}>
                        <p style={{ color: pubTheme.text, margin: '0 0 0.5rem', fontSize: '0.85rem' }}>Así se verán tus tarjetas y textos.</p>
                        <span style={{ display: 'inline-block', backgroundColor: pubTheme.primary, color: pubTheme.background, padding: '6px 14px', borderRadius: '7px', fontSize: '0.82rem', fontWeight: 700 }}>Botón</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                    <button onClick={savePublicTheme} disabled={savingTheme} style={btnStyle(savingTheme)}>
                      {savingTheme ? 'Guardando...' : 'Guardar colores'}
                    </button>
                    <button
                      onClick={resetPublicTheme}
                      disabled={savingTheme}
                      style={{ background: 'none', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.text, cursor: savingTheme ? 'not-allowed' : 'pointer', padding: '7px 16px', fontSize: '0.85rem' }}
                    >
                      Restablecer
                    </button>
                  </div>
                  {msgTheme && <p style={{ color: msgTheme.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.5rem', marginBottom: 0 }}>{msgTheme}</p>}
                </div>
              )}
            </div>

            {/* Domicilios */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '1rem' }}>Domicilios</h2>
              <label style={labelStyle}>Tarifa general (COP)</label>
              <input
                type="number"
                onWheel={e => e.currentTarget.blur()}
                value={domicileGeneral}
                onChange={e => setDomicileGeneral(e.target.value)}
                placeholder="ej: 5000"
                min="0"
                style={{ ...inputStyle, marginBottom: '1rem' }}
              />
              <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', marginBottom: '0.6rem' }}>
                Tarifas por ciudad (opcional). Si el cliente es de una ciudad listada, se usa esa tarifa; si no, la general.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.8rem' }}>
                {domicileCustom.map((row, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                    <input
                      type="text"
                      value={row.dept}
                      onChange={e => updateDomicileRow(i, 'dept', e.target.value)}
                      placeholder="Ciudad"
                      style={{ ...inputStyle, flex: isMobile ? '1 1 100%' : 1 }}
                    />
                    <input
                      type="number"
                      onWheel={e => e.currentTarget.blur()}
                      value={row.value}
                      onChange={e => updateDomicileRow(i, 'value', e.target.value)}
                      placeholder="Tarifa"
                      min="0"
                      style={{ ...inputStyle, width: isMobile ? 'auto' : '120px', flex: isMobile ? '1 1 0' : 'none' }}
                    />
                    <button
                      onClick={() => removeDomicileRow(i)}
                      title="Eliminar"
                      style={{ background: 'none', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.error, cursor: 'pointer', padding: '8px 12px', fontSize: '0.9rem', flexShrink: 0 }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={addDomicileRow}
                style={{ background: 'none', border: `1px solid ${theme.colors.primary}`, borderRadius: '7px', color: theme.colors.primary, cursor: 'pointer', padding: '7px 16px', fontSize: '0.85rem', marginBottom: '1rem' }}
              >
                + Agregar ciudad
              </button>
              <div>
                <button onClick={saveDomicile} disabled={savingDomicile} style={btnStyle(savingDomicile)}>
                  {savingDomicile ? 'Guardando...' : 'Guardar domicilios'}
                </button>
                {msgDomicile && <p style={{ color: msgDomicile.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.4rem' }}>{msgDomicile}</p>}
              </div>
            </div>

            {/* Agentes */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '1rem' }}>Agentes</h2>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={agentsCanStart}
                  disabled={savingStart}
                  onChange={e => saveAgentsCanStart(e.target.checked)}
                  style={{ accentColor: theme.colors.primary }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>Los agentes pueden iniciar conversación</span>
              </label>
              {msgStart && <p style={{ color: msgStart.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.5rem', marginBottom: 0 }}>{msgStart}</p>}
            </div>

            {/* Administrador: avisos cuando a un trabajador le agendan una cita */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '1rem' }}>Administrador</h2>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginBottom: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={notifAdminApp}
                  disabled={savingNotifAdmin}
                  onChange={e => saveNotifAdmin('notificar_admin_app', e.target.checked)}
                  style={{ accentColor: theme.colors.primary, marginTop: '3px' }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>
                  Activar notificaciones de aplicación para el administrador
                  <span style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '2px' }}>
                    Cada vez que le agenden una cita a un trabajador, el administrador también recibe la notificación.
                  </span>
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: isPremium ? 'pointer' : 'not-allowed', opacity: isPremium ? 1 : 0.55 }}>
                <input
                  type="checkbox"
                  checked={notifAdminCorreo}
                  disabled={savingNotifAdmin || (!isPremium && !notifAdminCorreo)}
                  onChange={e => {
                    // Al marcarlo se piden el correo y la contraseña API, y se guarda con
                    // el botón; al desmarcarlo se desactiva al instante.
                    if (e.target.checked) { setNotifAdminCorreo(true); setMsgNotifAdmin(''); }
                    else saveNotifAdmin('notificar_admin_correo', false);
                  }}
                  style={{ accentColor: theme.colors.primary, marginTop: '3px' }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>
                  Notificar al administrador las citas de los trabajadores vía correo electrónico
                  <span style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '2px' }}>
                    Llega un correo al correo con el que inicia sesión el administrador, con el cliente, día, hora, servicio y trabajador.
                  </span>
                  {!isPremium && (
                    <span style={{ display: 'inline-block', marginTop: '6px', color: '#facc15', fontSize: '0.78rem', fontWeight: 600 }}>
                      ★ Disponible solo para empresas premium
                    </span>
                  )}
                </span>
              </label>
              {notifAdminCorreo && isPremium && (
                <div style={{ marginTop: '0.8rem', paddingLeft: '24px' }}>
                  <label style={labelStyle}>Email desde el que se envían los avisos</label>
                  <input
                    type="email"
                    value={emailNotifAdmin}
                    onChange={e => setEmailNotifAdmin(e.target.value)}
                    placeholder="tu@gmail.com"
                    style={{ ...inputStyle, marginBottom: '0.7rem' }}
                  />
                  <label style={labelStyle}>Contraseña API del correo</label>
                  <input
                    type="password"
                    value={passNotifAdmin}
                    onChange={e => setPassNotifAdmin(e.target.value)}
                    placeholder={hasPassNotifAdmin ? '•••••••• (guardada; escribe otra para cambiarla)' : 'contraseña de aplicación'}
                    autoComplete="new-password"
                    style={{ ...inputStyle, marginBottom: '0.5rem' }}
                  />
                  <a href="https://www.youtube.com/watch?v=_Ep3ttp1Z3w" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary, fontSize: '0.8rem' }}>
                    🎬 ¿No sabes cómo generarla? Ver video →
                  </a>
                  <div style={{ marginTop: '0.8rem' }}>
                    <button onClick={saveCorreoAdmin} disabled={savingNotifAdmin} style={btnStyle(savingNotifAdmin)}>
                      {savingNotifAdmin ? 'Guardando...' : 'Guardar correo'}
                    </button>
                  </div>
                </div>
              )}
              {msgNotifAdmin && <p style={{ color: msgNotifAdmin.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.7rem', marginBottom: 0 }}>{msgNotifAdmin}</p>}
            </div>

            {/* Confirmación de cita: plantilla al cliente apenas agenda */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '1rem' }}>Confirmación de cita</h2>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: isPremium ? 'pointer' : 'not-allowed', opacity: isPremium ? 1 : 0.55 }}>
                <input
                  type="checkbox"
                  checked={confActiva}
                  disabled={savingConf || (!isPremium && !confActiva)}
                  onChange={e => {
                    const checked = e.target.checked;
                    setConfActiva(checked);
                    setMsgConf('');
                    if (!checked) saveConfirmacion(false);
                  }}
                  style={{ accentColor: theme.colors.primary, marginTop: '3px' }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>
                  Mandar plantilla con la información de la cita
                  <span style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '2px' }}>
                    Apenas alguien agenda, le llega por WhatsApp al celular que escribió en el formulario.
                  </span>
                  {!isPremium && (
                    <span style={{ display: 'block', marginTop: '6px', color: '#facc15', fontSize: '0.78rem', fontWeight: 600 }}>
                      ★ Disponible solo para empresas premium
                    </span>
                  )}
                </span>
              </label>
              <div style={{ marginTop: '0.7rem', padding: '8px 12px', borderRadius: '8px', backgroundColor: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.55)', color: '#fb923c', fontSize: '0.85rem', fontWeight: 600 }}>
                Cada envío cuesta ${costoPlantilla.toLocaleString('es-CO')} pesos
              </div>
              {confActiva && isPremium && (
                <div style={{ marginTop: '1rem' }}>
                  <TemplateVarsPicker
                    label="Plantilla a enviar"
                    templates={ticketTemplates}
                    templateId={confTemplateId}
                    onTemplateChange={id => { setConfTemplateId(id); setConfVars({}); setMsgConf(''); }}
                    mapping={confVars}
                    onMappingChange={setConfVars}
                    fields={customFields}
                    headers={headers}
                    companyParam={roleId === 1 && selectedCompanyId ? `?company_id=${selectedCompanyId}` : ''}
                  />
                  <div style={{ marginTop: '1rem' }}>
                    <button onClick={() => saveConfirmacion(true)} disabled={savingConf} style={btnStyle(savingConf)}>
                      {savingConf ? 'Guardando...' : 'Guardar confirmación'}
                    </button>
                  </div>
                </div>
              )}
              {msgConf && <p style={{ color: msgConf.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.7rem', marginBottom: 0 }}>{msgConf}</p>}
            </div>

            {/* Recordatorios de cita por plantilla de WhatsApp (solo premium) */}
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '1rem' }}>Recordatorios de cita</h2>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: isPremium ? 'pointer' : 'not-allowed', opacity: isPremium ? 1 : 0.55 }}>
                <input
                  type="checkbox"
                  checked={recActivo}
                  disabled={savingRec || (!isPremium && !recActivo)}
                  onChange={e => {
                    const checked = e.target.checked;
                    setRecActivo(checked);
                    setMsgRec('');
                    if (!checked) saveRecordatorio(false);
                  }}
                  style={{ accentColor: theme.colors.primary, marginTop: '3px' }}
                />
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>
                  Mandar recordatorio de cita a los clientes
                  {!isPremium && (
                    <span style={{ display: 'block', marginTop: '6px', color: '#facc15', fontSize: '0.78rem', fontWeight: 600 }}>
                      ★ Disponible solo para empresas premium
                    </span>
                  )}
                </span>
              </label>
              <div style={{ marginTop: '0.7rem', padding: '8px 12px', borderRadius: '8px', backgroundColor: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.55)', color: '#fb923c', fontSize: '0.85rem', fontWeight: 600 }}>
                Cada recordatorio cuesta ${costoPlantilla.toLocaleString('es-CO')} pesos
              </div>

              {recActivo && isPremium && (
                <div style={{ marginTop: '1rem' }}>
                  <TemplateVarsPicker
                    label="Plantilla del recordatorio"
                    templates={ticketTemplates}
                    templateId={recTemplateId}
                    onTemplateChange={id => { setRecTemplateId(id); setRecVars({}); setMsgRec(''); }}
                    mapping={recVars}
                    onMappingChange={setRecVars}
                    fields={customFields}
                    headers={headers}
                    companyParam={roleId === 1 && selectedCompanyId ? `?company_id=${selectedCompanyId}` : ''}
                  />

                  <label style={{ ...labelStyle, marginTop: '0.9rem' }}>¿Con cuántas horas de anticipación de la cita se manda el recordatorio?</label>
                  <input
                    type="number"
                    min="1"
                    max="72"
                    step="1"
                    value={recHoras}
                    onChange={e => setRecHoras(e.target.value)}
                    style={{ ...inputStyle, maxWidth: '140px' }}
                  />

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginTop: '1rem' }}>
                    <input
                      type="checkbox"
                      checked={respActiva}
                      onChange={e => setRespActiva(e.target.checked)}
                      style={{ accentColor: theme.colors.primary, marginTop: '3px' }}
                    />
                    <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>Activar respuesta de plantilla</span>
                  </label>
                  {respActiva && (
                    <div style={{ marginTop: '0.7rem', paddingLeft: '24px' }}>
                      <label style={labelStyle}>Email donde se te manda la respuesta</label>
                      <input
                        type="email"
                        value={emailResp}
                        onChange={e => setEmailResp(e.target.value)}
                        placeholder="tu@gmail.com"
                        style={{ ...inputStyle, marginBottom: '0.7rem' }}
                      />
                      <label style={labelStyle}>Contraseña API del correo</label>
                      <input
                        type="password"
                        value={passResp}
                        onChange={e => setPassResp(e.target.value)}
                        placeholder={hasPassResp ? '•••••••• (guardada; escribe otra para cambiarla)' : 'contraseña de aplicación'}
                        autoComplete="new-password"
                        style={{ ...inputStyle, marginBottom: '0.5rem' }}
                      />
                      <a href="https://www.youtube.com/watch?v=_Ep3ttp1Z3w" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary, fontSize: '0.8rem' }}>
                        🎬 ¿No sabes cómo generarla? Ver video →
                      </a>
                    </div>
                  )}

                  <div style={{ marginTop: '1rem' }}>
                    <button onClick={() => saveRecordatorio(true)} disabled={savingRec} style={btnStyle(savingRec)}>
                      {savingRec ? 'Guardando...' : 'Guardar recordatorios'}
                    </button>
                  </div>
                </div>
              )}
              {msgRec && <p style={{ color: msgRec.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.7rem', marginBottom: 0 }}>{msgRec}</p>}
            </div>

            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1.2rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1rem', marginBottom: '0.8rem' }}>Pasarela de pagos (Wompi)</h2>
              {info.has_pasarela && (
                <p style={{ color: theme.colors.success, fontSize: '0.85rem', marginBottom: '0.8rem' }}>✓ Clave configurada. Puedes actualizarla a continuación.</p>
              )}
              <label style={labelStyle}>{info.has_pasarela ? 'Nueva clave privada' : 'Clave privada de Wompi'}</label>
              <input type="password" value={pasarela} onChange={e => setPasarela(e.target.value)} placeholder="•••••••••••••••" style={{ ...inputStyle, marginBottom: '0.5rem' }} />
              <p style={{ color: theme.colors.textMuted, fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                Crea tu cuenta en Wompi:{' '}
                <a href="https://registro.wompi.co/es/onboarding/aggregator" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary }}>
                  registro.wompi.co →
                </a>
              </p>
              <p style={{ color: '#f59e0b', fontSize: '0.75rem', marginBottom: '0.8rem' }}>
                ⚠️ Cada pago realizado a través de esta plataforma tiene una comisión de 2,65%. Adicionalmente, el gobierno de Colombia cobra $700 pesos por cada transacción en concepto de impuestos. La creación de tu cuenta se verá reflejada en las próximas 24 horas.
              </p>
              <div style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', padding: '0.7rem 0.8rem', marginBottom: '0.8rem' }}>
                <label style={{ ...labelStyle, display: 'block', marginBottom: '0.35rem' }}>
                  Para que tu pasarela de pagos funcione bien, copia esta URL y pégala en el webhook de eventos de tu cuenta de Wompi. Corresponde al dominio en el que estás ahora ({window.location.hostname}):
                </label>
                <code style={{ display: 'block', color: theme.colors.primary, fontSize: '0.78rem', wordBreak: 'break-all', userSelect: 'all' }}>
                  {`${window.location.origin}/api/pay/client-webhook`}
                </code>
              </div>
              <button onClick={savePasarela} disabled={savingPasarela} style={btnStyle(savingPasarela)}>
                {savingPasarela ? 'Guardando...' : 'Guardar clave'}
              </button>
              {msgPasarela && <p style={{ color: msgPasarela.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', marginTop: '0.4rem' }}>{msgPasarela}</p>}
            </div>
          </>
        )}
      </div>
    </main>
  );
}

export default Empresa;
